import { Check as CheckIcon, Error as ErrorIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { StyledFormControl, StyledMenuItem } from "@/components/ui";
import type { TestCase } from "./types";

interface BatchSeverityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (severity: string) => void;
  selectedTests: TestCase[];
  isLoading: boolean;
}

export const BatchSeverityDialog: React.FC<BatchSeverityDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedTests,
  isLoading,
}) => {
  const [selectedSeverity, setSelectedSeverity] = useState<string>("");

  const severityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  useEffect(() => {
    if (open) {
      setSelectedSeverity("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (selectedSeverity) {
      onConfirm(selectedSeverity);
    }
  };

  const handleClose = () => {
    setSelectedSeverity("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ErrorIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
            Update Severity
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.875, pb: 0.875 }}>
        <Stack spacing={2.5}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Selected Tests ({selectedTests.length})
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              {selectedTests.slice(0, 5).map((test) => (
                <React.Fragment key={test.id}>
                  {test.name}
                  <br />
                </React.Fragment>
              ))}
              {selectedTests.length > 5 && (
                <Typography variant="caption" sx={{ fontSize: 11, fontStyle: "italic" }}>
                  ...and {selectedTests.length - 5} more
                </Typography>
              )}
            </Typography>
          </Paper>

          <StyledFormControl fullWidth size="small">
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
            >
              {severityOptions.map((option) => (
                <StyledMenuItem key={option.value} value={option.value}>
                  <Typography variant="body2" fontWeight={500}>
                    {option.label}
                  </Typography>
                </StyledMenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 0.875, justifyContent: "flex-end" }}>
        <IconButton
          onClick={handleConfirm}
          disabled={!selectedSeverity || isLoading}
          sx={{
            width: 40,
            height: 40,
            color: "grey.600",
            "&:hover": {
              bgcolor: "grey.300",
            },
            "&:disabled": {
              bgcolor: "grey.200",
              color: "grey.400",
            },
          }}
        >
          <CheckIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};
